/** @file Sets the animation on a base track, starting a crossfade from the current entry. */
import { invariant } from '@pose2d/std/invariant';
import { releaseFixLayer } from '../_internal/direction/releaseFixLayer';
import { startEntry } from '../_internal/startEntry';
import type { AnimState, TrackEntry } from '../contracts/state';
import type { SkeletonData } from '../skeletonData';
import { BASE_TRACKS } from './baseTracks.data';

/**
 * Replaces base track (an integer in [0, BASE_TRACKS)) with anim; tracks BASE_TRACKS + t belong to the mirrorFix
 * layer of base track t, which only core E's playDirectional plays (W2-12, R2-06). mixDuration defaults from
 * data.mixDefaults. The new entry mixes from the current one (keeping its from-chain) with fresh per-bone mixDirs;
 * a current entry that never advanced is dropped and its own `from` is used instead. With nothing to mix from, a
 * given mixDuration fades the entry in over the lower tracks on tracks 1+ (§5.7; the default 0 pops it in); on
 * track 0 it shows at once (R19-01). Clears the queue. The track's mirrorFix layer belongs to the directional entry
 * it replaces (R19-02): it fades out with it over the mix and leaves when it is disposed, at once for a mix of 0 or a
 * never-played owner. stepState emits interrupt/start on the next step. NOT_FOUND at `anim`, before anything changes.
 */
export function setAnimation(state: AnimState, data: SkeletonData, track: number, anim: string, opts?: { loop?: boolean; mixDuration?: number }): TrackEntry {
  invariant(Number.isInteger(track) && track >= 0 && track < BASE_TRACKS, 'track must be an integer in [0, BASE_TRACKS); tracks BASE_TRACKS + t belong to playDirectional');
  const entry = startEntry(state, data, track, anim, opts);
  releaseFixLayer(state, track);
  return entry;
}
